import React, { useState, useEffect } from 'react';
import { api } from './api';
import './Profile.css';

export default function Profile({ user, onClose, onUpdate, onOpenAdminPanel }) {
  const [isOpen, setIsOpen] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    middle_name: '',
    avatar: null,
  });
  const [avatarPreview, setAvatarPreview] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        middle_name: user.middle_name || '',
        avatar: null,
      });
      setAvatarPreview(user.avatar_url || user.avatar || null);
    }
  }, [user]);

  const handleClose = () => {
    setIsClosing(true);
    setIsEditing(false);
    setError(null);
    setTimeout(() => {
      setIsOpen(false);
      onClose();
    }, 300);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        avatar: file,
      }));
      // Создаем превью
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const updatedUser = await api.updateCurrentUser(formData);
      setIsEditing(false);
      if (onUpdate) {
        onUpdate(updatedUser);
      }
    } catch (err) {
      setError(err.message || 'Ошибка при сохранении данных');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`profile-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div className={`profile-modal ${isClosing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        <button className="profile-close" onClick={handleClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <div className="profile-header">
          <div className="profile-avatar-container">
            <div className="profile-avatar">
              {avatarPreview ? (
                <img src={avatarPreview} alt={user?.username} />
              ) : (
                <div className="avatar-placeholder">
                  {user?.username?.charAt(0).toUpperCase() || 'У'}
                </div>
              )}
            </div>
            {isEditing && (
              <label className="avatar-upload-button">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  style={{ display: 'none' }}
                />
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
              </label>
            )}
          </div>
          <h2 className="profile-name">
            {isEditing ? (
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className="profile-input"
                placeholder="Имя пользователя"
              />
            ) : (
              user?.full_name || user?.username || 'Пользователь'
            )}
          </h2>
          <span className="profile-role">
            {user?.role === 'admin' ? 'Администратор' : 'Учитель'}
          </span>
        </div>

        <div className="profile-content">
          {error && (
            <div className="profile-error">
              {error}
            </div>
          )}

          {isEditing ? (
            <form onSubmit={handleSubmit} className="profile-form fade-in">
              <div className="form-group">
                <label>Имя</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className="profile-input"
                  placeholder="Введите имя"
                />
              </div>

              <div className="form-group">
                <label>Фамилия</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className="profile-input"
                  placeholder="Введите фамилию"
                />
              </div>

              <div className="form-group">
                <label>Отчество</label>
                <input
                  type="text"
                  name="middle_name"
                  value={formData.middle_name}
                  onChange={handleInputChange}
                  className="profile-input"
                  placeholder="Введите отчество"
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="profile-input"
                  placeholder="Введите email"
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="profile-button profile-button-cancel"
                  onClick={() => {
                    setIsEditing(false);
                    setError(null);
                    // Восстанавливаем исходные данные
                    if (user) {
                      setFormData({
                        username: user.username || '',
                        email: user.email || '',
                        first_name: user.first_name || '',
                        last_name: user.last_name || '',
                        middle_name: user.middle_name || '',
                        avatar: null,
                      });
                      setAvatarPreview(user.avatar_url || user.avatar || null);
                    }
                  }}
                  disabled={loading}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="profile-button profile-button-save"
                  disabled={loading}
                >
                  {loading ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </form>
          ) : (
            <div className="profile-info fade-in">
              <div className="info-item">
                <label>Имя пользователя</label>
                <p>{user?.username || '-'}</p>
              </div>
              <div className="info-item">
                <label>Имя</label>
                <p>{user?.first_name || '-'}</p>
              </div>
              <div className="info-item">
                <label>Фамилия</label>
                <p>{user?.last_name || '-'}</p>
              </div>
              <div className="info-item">
                <label>Email</label>
                <p>{user?.email || '-'}</p>
              </div>
              <div className="info-item">
                <label>Роль</label>
                <p>{(user?.role === 'admin' || user?.is_admin || user?.is_superuser) ? 'Администратор' : 'Учитель'}</p>
              </div>
              {(user?.role === 'admin' || user?.is_admin || user?.is_superuser) && onOpenAdminPanel && (
                <button
                  className="profile-button profile-button-admin"
                  onClick={() => {
                    onClose();
                    setTimeout(() => onOpenAdminPanel(), 300);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                    <path d="M2 17l10 5 10-5"></path>
                    <path d="M2 12l10 5 10-5"></path>
                  </svg>
                  Админ-панель
                </button>
              )}
              <button
                className="profile-button profile-button-edit"
                onClick={() => setIsEditing(true)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
                Редактировать профиль
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

